// Conversation-Level Dialogue Blueprint
// This file defines the static dialogue map — all possible states of the 
// conversation and how they connect. It contains no logic, side effects, 
// or UI actions. Each entry only specifies:
//   - The state name
//   - The valid triggers from that state (typed as a union)
//   - The next state each trigger leads to
//   - Optional metadata such as entry prompts or required slots

export type ConversationDialogueState = 
  | 'conv_welcome'
  | 'conv_help'
  | 'conv_something_else'
  | 'conv_task_request'
  | 'conv_task_simple_splitting_and_confirmation'
  | 'conv_defined_task_classification'
  | 'well_defined_tasks_explanation'
  | 'conv_task_processing'
  | 'are_there_more_tasks'
  | 'assignement_additional_comments'
  | 'final_confirmation'
  | 'user_requests_correction'
  | 'delete_a_task'
  | 'change_a_task'
  | 'conv_bye'

// Typed trigger union for compiler-checked transitions
export type ConversationTrigger = 
  | 'USER_SUBMITS_TASK'
  | 'USER_WANTS_TO_SUBMIT_TASK'
  | 'USER_CONFIRMS_TASK_SUBMISSION'
  | 'USER_REJECTS_TASK_SUBMISSION'
  | 'USER_CONFIRMS'
  | 'USER_REJECTS'
  | 'USER_SAYS_NO'
  | 'USER_SAYS_HELLO'
  | 'USER_SAYS_GOODBYE'
  | 'USER_ASKS_FOR_HELP'
  | 'USER_ASKS_ABOUT_DEFINED_TASKS'
  | 'USER_SAYS_SOMETHING_ELSE'
  | 'USER_RETYPES_TASK'
  | 'USER_SUBMITS_TASKS'
  | 'USER_WANTS_TO_DELETE_TASK'
  | 'USER_WANTS_TO_CHANGE_TASK'
  | 'USER_ACCEPTS_ALL_TASKS'
  | 'USER_CANCELS_DELETION'
  | 'USER_CANCELS_CHANGE'
  | 'TASK_DELETED'
  | 'TASK_CHANGED'
  | 'ALL_TASKS_COMPLETED'
  | 'USER_SUBMITS_ASSIGNEMENT_COMMENT'

// Optional metadata for each state
export interface ConversationStateMetadata {
  entryPromptKey?: string;
  requiredSlots?: string[];
  isTerminal?: boolean;
  entryActionKeys?: string[];
}

// Pure static blueprint of conversation flow
export const CONVERSATION_STATE_TRANSITIONS: Record<ConversationDialogueState, Partial<Record<ConversationTrigger, ConversationDialogueState>>> = {
  conv_welcome: {
    'USER_SUBMITS_TASK': 'conv_task_simple_splitting_and_confirmation',
    'USER_WANTS_TO_SUBMIT_TASK': 'conv_task_request',
    'USER_SAYS_SOMETHING_ELSE': 'conv_something_else',
    'USER_SAYS_GOODBYE': 'conv_bye',
    'USER_SAYS_HELLO': 'conv_welcome',
    'USER_ASKS_FOR_HELP': 'conv_help',
    'USER_ASKS_ABOUT_DEFINED_TASKS': 'well_defined_tasks_explanation'
  },
  
  conv_help: {
    'USER_SAYS_GOODBYE': 'conv_bye',
    'USER_SAYS_SOMETHING_ELSE': 'conv_something_else',
    'USER_SAYS_HELLO': 'conv_welcome',
    'USER_CONFIRMS': 'conv_welcome'
  },
  
  conv_task_request: {
    'USER_SUBMITS_TASK': 'conv_task_simple_splitting_and_confirmation',
    'USER_ASKS_ABOUT_DEFINED_TASKS': 'well_defined_tasks_explanation',
    'USER_SAYS_SOMETHING_ELSE': 'conv_something_else',
    'USER_SAYS_GOODBYE': 'conv_bye',
    'USER_SAYS_HELLO': 'conv_welcome'
  },

  conv_task_simple_splitting_and_confirmation: {
    'USER_CONFIRMS_TASK_SUBMISSION': 'conv_task_processing',
    'USER_REJECTS_TASK_SUBMISSION': 'conv_task_request',
    'USER_SAYS_SOMETHING_ELSE': 'conv_something_else',
    'USER_SAYS_GOODBYE': 'conv_bye',
    'USER_SAYS_HELLO': 'conv_welcome',
    'USER_RETYPES_TASK': 'conv_task_simple_splitting_and_confirmation'
  },

  conv_defined_task_classification: {
    'USER_SAYS_GOODBYE': 'conv_bye',
    'USER_SAYS_HELLO': 'conv_welcome',
    'USER_SAYS_SOMETHING_ELSE': 'conv_something_else',
    'ALL_TASKS_COMPLETED': 'are_there_more_tasks'
  },

  conv_task_processing: {
    'USER_SAYS_GOODBYE': 'conv_bye',
    'USER_SAYS_HELLO': 'conv_welcome',
    'USER_SAYS_SOMETHING_ELSE': 'conv_something_else',
    'ALL_TASKS_COMPLETED': 'are_there_more_tasks'
  },

  are_there_more_tasks: {
    'USER_SAYS_NO': 'assignement_additional_comments',
    'USER_CONFIRMS': 'conv_task_request',
    'USER_SUBMITS_TASKS': 'conv_task_simple_splitting_and_confirmation',
    'USER_SAYS_GOODBYE': 'conv_bye',
    'USER_SAYS_SOMETHING_ELSE': 'conv_something_else'
  },

  assignement_additional_comments: {
    'USER_CONFIRMS': 'conv_bye',
    'USER_SUBMITS_ASSIGNEMENT_COMMENT': 'conv_bye',
    'USER_SAYS_GOODBYE': 'conv_bye',
    'USER_SAYS_SOMETHING_ELSE': 'conv_something_else'
  },

  final_confirmation: {
    'USER_CONFIRMS': 'are_there_more_tasks',
    'USER_REJECTS': 'user_requests_correction',
    'USER_SAYS_GOODBYE': 'conv_bye',
    'USER_SAYS_SOMETHING_ELSE': 'conv_something_else'
  },

  user_requests_correction: {
    'USER_WANTS_TO_DELETE_TASK': 'delete_a_task',
    'USER_WANTS_TO_CHANGE_TASK': 'change_a_task',
    'USER_ACCEPTS_ALL_TASKS': 'conv_bye',
    'USER_SAYS_SOMETHING_ELSE': 'conv_something_else'
  },

  delete_a_task: {
    'TASK_DELETED': 'final_confirmation',
    'USER_CANCELS_DELETION': 'final_confirmation',
    'USER_SAYS_GOODBYE': 'conv_bye',
    'USER_SAYS_SOMETHING_ELSE': 'conv_something_else'
  },

  change_a_task: {
    'TASK_CHANGED': 'final_confirmation',
    'USER_CANCELS_CHANGE': 'final_confirmation',
    'USER_SAYS_GOODBYE': 'conv_bye',
    'USER_SAYS_SOMETHING_ELSE': 'conv_something_else'
  },

  well_defined_tasks_explanation: {
    'USER_SUBMITS_TASK': 'conv_task_simple_splitting_and_confirmation',
    'USER_WANTS_TO_SUBMIT_TASK': 'conv_task_request',
    'USER_SAYS_SOMETHING_ELSE': 'conv_something_else',
    'USER_SAYS_GOODBYE': 'conv_bye',
    'USER_SAYS_HELLO': 'conv_welcome',
    'USER_ASKS_FOR_HELP': 'conv_help',
    'USER_ASKS_ABOUT_DEFINED_TASKS': 'well_defined_tasks_explanation'
  },

  conv_something_else: {
    'USER_SAYS_GOODBYE': 'conv_bye',
    'USER_SAYS_SOMETHING_ELSE': 'conv_something_else'
  },
  
  conv_bye: {
    // Terminal state - no transitions
  }
};

// Optional metadata for dialogue manager
export const CONVERSATION_STATE_METADATA: Record<ConversationDialogueState, ConversationStateMetadata> = {
  conv_welcome: {
    requiredSlots: [],
    entryActionKeys: ['greet_user']
  },
  conv_help: {
    requiredSlots: [],
    entryActionKeys: ['provide_help']
  },
  conv_something_else: {
    entryPromptKey: 'something_else_message',
    requiredSlots: []
  },
  conv_task_request: {
    requiredSlots: ['task_description'],
    entryActionKeys: ['ask_task_description']
  },
  conv_task_simple_splitting_and_confirmation: {
    requiredSlots: ['task_description'],
    entryActionKeys: ['confirm_review']
  },
  conv_defined_task_classification: {
    requiredSlots: [],
    entryActionKeys: ['show_defined_task_message']
  },
  well_defined_tasks_explanation: {
    requiredSlots: [],
    entryActionKeys: ['show_well_defined_tasks_list']
  },
  conv_task_processing: {
    entryPromptKey: 'conv_task_processing',
    requiredSlots: []
  },
  are_there_more_tasks: {
    entryPromptKey: 'are_there_more_tasks',
    requiredSlots: []
  },
  assignement_additional_comments: {
    entryPromptKey: 'assignement_additional_comments',
    requiredSlots: []
  },
  final_confirmation: {
    entryPromptKey: 'final_confirmation',
    requiredSlots: []
  },
  user_requests_correction: {
    entryPromptKey: 'user_requests_correction',
    requiredSlots: []
  },
  delete_a_task: {
    entryPromptKey: 'deleteTaskConfirmation',
    requiredSlots: []
  },
  change_a_task: {
    entryPromptKey: 'changeTaskConfirmation',
    requiredSlots: []
  },
  conv_bye: {
    entryPromptKey: 'conv_bye',
    entryActionKeys: ['submit_conversation'],
    requiredSlots: [],
    isTerminal: true
  }
};

// Pure helper functions - no side effects
export function getNextConversationState(
  currentState: ConversationDialogueState,
  trigger: ConversationTrigger
): ConversationDialogueState | null {
  const transitions = CONVERSATION_STATE_TRANSITIONS[currentState];
  return transitions[trigger] || null;
}

export function isValidConversationTransition(
  currentState: ConversationDialogueState,
  trigger: ConversationTrigger
): boolean {
  const nextState = getNextConversationState(currentState, trigger);
  return nextState !== null && nextState !== currentState;
}

export function getConversationStateMetadata(
  state: ConversationDialogueState
): ConversationStateMetadata {
  return CONVERSATION_STATE_METADATA[state];
} 